<template>
    <div class="app-bar d-flex align-center justify-space-between">
        <!-- Logo -->
        <div class="d-flex flex-grow-1 flex-shrink-0" style="width: 33.33%">
            <img
                alt="Esenda logo"
                :src="esendaLogoSvg"
                class="esenda-logo"
                width="100"
                height="21"
            />
        </div>

        <!-- Temporary placeholder for additional controls -->
        <div
            class="d-flex ga-2 justify-end align-center flex-grow-1 flex-shrink-0"
            style="width: 66.66%"
        >
            <v-btn
                v-if="xs"
                class="help-button no-print"
                aria-label="Help"
                variant="flat"
                @click="openHelp"
            >
                <v-icon size="24" :icon="mdiHelpCircleOutline" />
            </v-btn>

            <preferences-drawer @on-country-changed="onCountryChanged" />

            <!-- Logged in user -->
            <logged-in-user
                v-if="isAuthenticated"
                :user-picture="''"
                :user-name="username"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
    useCheckoutStore,
    usePaymentsStore,
    useSessionStore,
} from "@/plugins/store";

import loggedInUser from "@/components/group/loggedInUser.vue";
import preferencesDrawer from "@/components/base/preferencesDrawer.vue";

import esendaLogoSvg from "@/assets/logos/Esenda_logo_darkblue.svg";
import { getCurrentAuthenticatedUser } from "@/helpers/auth";
import { getPaymentMethodsList } from "@/apis/esendaV2/organisations";
import { useRoute } from "vue-router";
import { routeNames } from "@/constants/routeNames";
import { mdiHelpCircleOutline } from "@mdi/js";
import { useDisplay } from "vuetify";
import { redirectToPaymentMethod } from "@/helpers/paymentMethodHelpers";

const sessionStore = useSessionStore();
const checkoutStore = useCheckoutStore();
const paymentStore = usePaymentsStore();
const route = useRoute();
const { xs } = useDisplay();

const isAuthenticated = computed(() => sessionStore.isAuthenticated);
const getUser = async () => {
    if (sessionStore.isAuthenticated) {
        const currentUser = await getCurrentAuthenticatedUser();
        if (currentUser && currentUser.username)
            sessionStore.setEmailAddress(currentUser.username);
    }
};
getUser();

const username = computed(() => sessionStore.emailAddress ?? "");

const onCountryChanged = async () => {
    const refreshPaymentMethodRoutes = [
        routeNames.PAYMENT_METHOD_SELECTION,
        routeNames.DISABLED_PAYMENT_METHODS,
    ];

    if (refreshPaymentMethodRoutes.includes(route.name as routeNames)) {
        paymentStore.loadingPaymentMethods = true;

        try {
            const paymentMethods = await getPaymentMethodsList(
                sessionStore.countryCode || "GB",
                checkoutStore.supplementaryData.amount,
            );

            if (paymentMethods) {
                redirectToPaymentMethod(paymentMethods);
            }
        } finally {
            paymentStore.loadingPaymentMethods = false;
        }
    }
};

const openHelp = () => {
    window.Beacon && window.Beacon("toggle");
};
</script>

<style scoped>
.app-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;
    /* box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.1); */
    border: 1px solid var(--surface-disabled);
    background-color: var(--white);
    height: 56px;
    padding: 0 16px;
}

.app-bar-center-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
}

.app-bar-center-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    max-width: 50%;
    margin-top: 14px;
}

.app-bar-center-text-width--authorised-user {
    max-width: 30%;
}

.app-bar-center-text--unauthorised-user {
    max-width: 50%;
}

.country-code {
    font-size: 0.875rem;
    line-height: 1;
}

.help-button {
    border-radius: var(--radius-container);
    padding: 0;
    height: 32px !important;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    line-height: 32px;
    display: flex;
    background: var(--white);
    color: var(--action-primary);
    width: 40px !important;
    min-width: unset !important;
}
</style>
